import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { AlertCircle, CheckCircle, Loader2, Link2 } from "lucide-react";
import { Alert, AlertDescription } from "../components/ui/alert";
import { axiosInstance } from "../App";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Input } from "../components/ui/input";

export default function EmergencyTools() {
  const [openRegisters, setOpenRegisters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(null);
  const [message, setMessage] = useState(null);
  const [user, setUser] = useState(null);

  // Orphan bank transactions reconciliation state
  const [orphans, setOrphans] = useState([]);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [selectedOrphans, setSelectedOrphans] = useState({});
  const [boxes, setBoxes] = useState([]);
  const [targetBoxId, setTargetBoxId] = useState("");
  const [reconcilePassword, setReconcilePassword] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  useEffect(() => {
    // Get user from localStorage
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");
    
    if (!token || !userData) {
      setMessage({ type: 'error', text: 'Debes iniciar sesión como MASTER' });
      setLoading(false);
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      if (parsedUser.role !== 'master') {
        setMessage({ type: 'error', text: 'Solo el MASTER puede usar estas herramientas de emergencia' });
        setLoading(false);
        return;
      }

      fetchOpenRegisters();
      fetchOrphans();
      fetchBoxes();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al verificar usuario' });
      setLoading(false);
    }
  }, []);

  const fetchOpenRegisters = async () => {
    try {
      const res = await axiosInstance.get("/emergency/list-open-registers");
      setOpenRegisters(res.data.registers || []);
      setLoading(false);
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
      setLoading(false);
    }
  };

  const fetchOrphans = async () => {
    setOrphansLoading(true);
    try {
      const res = await axiosInstance.get("/admin/orphan-bank-transactions");
      setOrphans(res.data.orphans || []);
    } catch (err) {
      setMessage({ type: 'error', text: `Error cargando huérfanas: ${err.response?.data?.detail || err.message}` });
    } finally {
      setOrphansLoading(false);
    }
  };

  const fetchBoxes = async () => {
    try {
      const res = await axiosInstance.get("/boxes");
      setBoxes(res.data || []);
    } catch (err) {
      // Silent fail, box selector simply empty
    }
  };

  const toggleOrphan = (id) => {
    setSelectedOrphans((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAllOrphans = () => {
    const allSelected = orphans.every((o) => selectedOrphans[o.id]);
    if (allSelected) {
      setSelectedOrphans({});
    } else {
      const next = {};
      orphans.forEach((o) => { next[o.id] = true; });
      setSelectedOrphans(next);
    }
  };

  const handleReconcile = async () => {
    const ids = Object.entries(selectedOrphans).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) {
      setReconcileResult({ type: 'error', text: 'Selecciona al menos una transacción huérfana' });
      return;
    }
    if (!targetBoxId) {
      setReconcileResult({ type: 'error', text: 'Selecciona la caja destino' });
      return;
    }
    if (!reconcilePassword) {
      setReconcileResult({ type: 'error', text: 'Ingresa la contraseña Master' });
      return;
    }
    if (!window.confirm(`¿Vincular ${ids.length} salidas huérfanas a la caja seleccionada? Esta acción crea registros históricos.`)) return;

    setReconciling(true);
    setReconcileResult(null);
    try {
      const res = await axiosInstance.post("/admin/link-orphan-bank-transactions", {
        orphan_ids: ids,
        target_box_id: targetBoxId,
        password: reconcilePassword,
      });
      setReconcileResult({
        type: 'success',
        text: `Vinculadas: ${res.data.linked_count}, Omitidas: ${res.data.skipped_count}`,
        details: res.data,
      });
      setSelectedOrphans({});
      setReconcilePassword("");
      fetchOrphans();
    } catch (err) {
      setReconcileResult({ type: 'error', text: `Error: ${err.response?.data?.detail || err.message}` });
    } finally {
      setReconciling(false);
    }
  };

  const handleForceClose = async (registerId, boxName) => {
    if (!window.confirm(`¿Estás seguro de querer FORZAR el cierre de la caja ${boxName}?`)) {
      return;
    }

    setClosing(registerId);
    setMessage(null);

    try {
      const res = await axiosInstance.post(`/emergency/force-close-register?register_id=${registerId}`);
      setMessage({
        type: 'success',
        text: `✓ Caja ${boxName} cerrada exitosamente. Refresca la página del Dashboard.`
      });
      setTimeout(() => { fetchOpenRegisters(); }, 1000);
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    } finally {
      setClosing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!user || user.role !== 'master') {
    return (
      <div className="container mx-auto p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Solo el MASTER puede acceder a estas herramientas de emergencia.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-red-600">
            🚨 Herramientas de Emergencia
          </CardTitle>
          <CardDescription>
            Usar solo cuando una caja esté atascada y no se pueda cerrar normalmente
          </CardDescription>
        </CardHeader>
        <CardContent>
          {message && (
            <Alert variant={message.type === 'error' ? 'destructive' : 'default'} className="mb-4">
              {message.type === 'error' ? (
                <AlertCircle className="h-4 w-4" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              <AlertDescription>{message.text}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              Cajas Abiertas/Preparadas: {openRegisters.length}
            </h3>

            {openRegisters.length === 0 ? (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  ✓ No hay cajas atascadas. Todas las cajas están cerradas correctamente.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                {openRegisters.map((register) => (
                  <Card key={register.id} className="border-orange-200">
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-lg">{register.box_name}</p>
                          <p className="text-sm text-slate-500">Status: {register.status}</p>
                          <p className="text-sm text-slate-500">
                            Abierta: {new Date(register.opened_at).toLocaleString('es-CR')}
                          </p>
                          {register.prepared_at && (
                            <p className="text-sm text-slate-500">
                              Preparada: {new Date(register.prepared_at).toLocaleString('es-CR')}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="destructive"
                          onClick={() => handleForceClose(register.id, register.box_name)}
                          disabled={closing === register.id}
                        >
                          {closing === register.id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Cerrando...
                            </>
                          ) : (
                            'Forzar Cierre'
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <Alert className="mt-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Instrucciones:</strong>
                <ol className="list-decimal ml-4 mt-2 space-y-1">
                  <li>Haz clic en "Forzar Cierre" para cerrar la caja atascada</li>
                  <li>Espera el mensaje de confirmación</li>
                  <li>Ve al Dashboard y refresca la página (Ctrl+F5)</li>
                  <li>Deberías ver "No hay caja abierta" y el botón "Abrir Caja"</li>
                </ol>
              </AlertDescription>
            </Alert>
          </div>
        </CardContent>
      </Card>

      {/* Orphan Bank Transactions Reconciliation */}
      <Card className="mt-6" data-testid="orphan-bank-tx-card">
        <CardHeader>
          <CardTitle className="text-2xl text-amber-700 flex items-center gap-2">
            <Link2 className="w-6 h-6" />
            Reconciliar salidas de banco huérfanas
          </CardTitle>
          <CardDescription>
            Vincula salidas de banco antiguas (sin caja registradora asociada) a una caja específica para que aparezcan en los reportes diarios históricos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {reconcileResult && (
            <Alert variant={reconcileResult.type === 'error' ? 'destructive' : 'default'} className="mb-4">
              {reconcileResult.type === 'error' ? <AlertCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
              <AlertDescription>
                <div>{reconcileResult.text}</div>
                {reconcileResult.details?.skipped?.length > 0 && (
                  <ul className="mt-2 text-xs list-disc ml-4">
                    {reconcileResult.details.skipped.map((s, i) => (
                      <li key={i}>{s.id?.slice(0, 8)} - {s.reason}</li>
                    ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}

          {orphansLoading ? (
            <div className="flex justify-center p-4"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : orphans.length === 0 ? (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>No hay salidas huérfanas. Todo limpio.</AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">{orphans.length} salidas huérfanas encontradas</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleAllOrphans}
                  data-testid="orphan-select-all-btn"
                >
                  {orphans.every((o) => selectedOrphans[o.id]) ? 'Deseleccionar todas' : 'Seleccionar todas'}
                </Button>
              </div>

              <div className="border rounded-md max-h-80 overflow-y-auto divide-y">
                {orphans.map((o) => (
                  <label
                    key={o.id}
                    className="flex items-center gap-3 p-2 hover:bg-slate-50 cursor-pointer"
                    data-testid={`orphan-row-${o.id}`}
                  >
                    <Checkbox
                      checked={!!selectedOrphans[o.id]}
                      onCheckedChange={() => toggleOrphan(o.id)}
                    />
                    <div className="flex-1 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">{o.bank_name}</span>
                        <span className="text-slate-500">{(o.created_at || '').slice(0, 10)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-slate-600">
                        <span>{o.description || '(sin descripción)'}</span>
                        <span>
                          {o.amount_colones > 0 && `₡${Number(o.amount_colones).toLocaleString('es-CR')}`}
                          {o.amount_dollars > 0 && ` $${Number(o.amount_dollars).toLocaleString('es-CR')}`}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="space-y-2 pt-2 border-t">
                <div>
                  <label className="text-sm font-medium">Caja destino</label>
                  <Select value={targetBoxId} onValueChange={setTargetBoxId}>
                    <SelectTrigger data-testid="target-box-select">
                      <SelectValue placeholder="Selecciona la caja a la que pertenecen estas salidas" />
                    </SelectTrigger>
                    <SelectContent>
                      {boxes.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Contraseña Master</label>
                  <Input
                    type="password"
                    value={reconcilePassword}
                    onChange={(e) => setReconcilePassword(e.target.value)}
                    placeholder="••••"
                    data-testid="reconcile-password-input"
                  />
                </div>
                <Button
                  onClick={handleReconcile}
                  disabled={reconciling}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  data-testid="reconcile-orphans-btn"
                >
                  {reconciling ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reconciliando...</>
                  ) : (
                    <>Vincular seleccionadas a la caja</>
                  )}
                </Button>
                <p className="text-xs text-slate-500">
                  Las salidas se asociarán al registro de la caja abierto en su fecha de creación. Si no había caja abierta ese día, se omitirán.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
